import { commitMutation, fetchQuery, graphql, GraphQLTaggedNode } from 'react-relay';
import { MutationParameters, OperationType } from 'relay-runtime';
import RelayEnvironment from 'src/relay/environment';
import { GenerationResult, ToolType, VideoResult } from './types';

// Import generated types
import { tasksAdGeneratorTaskStatusQuery, tasksAdGeneratorTaskStatusQuery$data } from './__generated__/tasksAdGeneratorTaskStatusQuery.graphql';
import { tasksMagicSwapTaskStatusQuery, tasksMagicSwapTaskStatusQuery$data } from './__generated__/tasksMagicSwapTaskStatusQuery.graphql';
import { tasksMultiImageFusionTaskStatusQuery, tasksMultiImageFusionTaskStatusQuery$data } from './__generated__/tasksMultiImageFusionTaskStatusQuery.graphql';
import { tasksProductStagingTaskStatusQuery, tasksProductStagingTaskStatusQuery$data } from './__generated__/tasksProductStagingTaskStatusQuery.graphql';
import { AdGeneratorInput, tasksStartAdGeneratorMutation } from './__generated__/tasksStartAdGeneratorMutation.graphql';
import { MagicSwapInput, tasksStartMagicSwapMutation } from './__generated__/tasksStartMagicSwapMutation.graphql';
import { MultiImageFusionInput, tasksStartMultiImageFusionMutation } from './__generated__/tasksStartMultiImageFusionMutation.graphql';
import { ProductStagingInput, tasksStartProductStagingMutation } from './__generated__/tasksStartProductStagingMutation.graphql';
import { tasksStartUpscalerMutation, UpscalerInput } from './__generated__/tasksStartUpscalerMutation.graphql';
import { tasksStartVideoGenerationMutation, VideoGenerationInput } from './__generated__/tasksStartVideoGenerationMutation.graphql';
import { tasksStartVirtualTryOnMutation, VirtualTryOnInput } from './__generated__/tasksStartVirtualTryOnMutation.graphql';
import { tasksUpscalerTaskStatusQuery, tasksUpscalerTaskStatusQuery$data } from './__generated__/tasksUpscalerTaskStatusQuery.graphql';
import { tasksVideoGenerationTaskStatusQuery, tasksVideoGenerationTaskStatusQuery$data } from './__generated__/tasksVideoGenerationTaskStatusQuery.graphql';
import { tasksVirtualTryOnTaskStatusQuery, tasksVirtualTryOnTaskStatusQuery$data } from './__generated__/tasksVirtualTryOnTaskStatusQuery.graphql';
import { ModelGeneration3DInput, tasksStartModelGeneration3DMutation } from './__generated__/tasksStartModelGeneration3DMutation.graphql';
import {
    tasksModelGeneration3DTaskStatusQuery,
    tasksModelGeneration3DTaskStatusQuery$data
} from './__generated__/tasksModelGeneration3DTaskStatusQuery.graphql';

// Re-export generated input types for external use
export type {
    AdGeneratorInput,
    MagicSwapInput,
    ModelGeneration3DInput,
    MultiImageFusionInput,
    ProductStagingInput,
    UpscalerInput,
    VideoGenerationInput,
    VirtualTryOnInput
};

// ============================================
// GraphQL Mutations
// ============================================

const StartVirtualTryOnMutation = graphql`
    mutation tasksStartVirtualTryOnMutation($input: VirtualTryOnInput!) {
        startVirtualTryOn(input: $input) {
            taskId
            state
            createdAt
        }
    }
`;

const StartMagicSwapMutation = graphql`
    mutation tasksStartMagicSwapMutation($input: MagicSwapInput!) {
        startMagicSwap(input: $input) {
            taskId
            state
            createdAt
        }
    }
`;

const StartMultiImageFusionMutation = graphql`
    mutation tasksStartMultiImageFusionMutation($input: MultiImageFusionInput!) {
        startMultiImageFusion(input: $input) {
            taskId
            state
            createdAt
        }
    }
`;

const StartAdGeneratorMutation = graphql`
    mutation tasksStartAdGeneratorMutation($input: AdGeneratorInput!) {
        startAdGenerator(input: $input) {
            taskId
            state
            createdAt
        }
    }
`;

const StartVideoGenerationMutation = graphql`
    mutation tasksStartVideoGenerationMutation($input: VideoGenerationInput!) {
        startVideoGeneration(input: $input) {
            taskId
            state
            createdAt
        }
    }
`;

const StartProductStagingMutation = graphql`
    mutation tasksStartProductStagingMutation($input: ProductStagingInput!) {
        startProductStaging(input: $input) {
            taskId
            state
            createdAt
        }
    }
`;

const StartUpscalerMutation = graphql`
    mutation tasksStartUpscalerMutation($input: UpscalerInput!) {
        startUpscaler(input: $input) {
            taskId
            state
            createdAt
        }
    }
`;

const StartModelGeneration3DMutation = graphql`
    mutation tasksStartModelGeneration3DMutation($input: ModelGeneration3DInput!) {
        startModelGeneration3D(input: $input) {
            taskId
            state
            createdAt
        }
    }
`;

// ============================================
// GraphQL Queries for Task Status
// ============================================

const VirtualTryOnTaskStatusQuery = graphql`
    query tasksVirtualTryOnTaskStatusQuery($taskId: Long!) {
        virtualTryOnTaskStatus(taskId: $taskId) {
            taskId
            state
            resultUrl
            errorMessage
            processingTimeMs
            createdAt
            completedAt
        }
    }
`;

const MagicSwapTaskStatusQuery = graphql`
    query tasksMagicSwapTaskStatusQuery($taskId: Long!) {
        magicSwapTaskStatus(taskId: $taskId) {
            taskId
            state
            resultUrl
            errorMessage
            processingTimeMs
            createdAt
            completedAt
        }
    }
`;

const MultiImageFusionTaskStatusQuery = graphql`
    query tasksMultiImageFusionTaskStatusQuery($taskId: Long!) {
        multiImageFusionTaskStatus(taskId: $taskId) {
            taskId
            state
            resultUrl
            errorMessage
            processingTimeMs
            createdAt
            completedAt
        }
    }
`;

const AdGeneratorTaskStatusQuery = graphql`
    query tasksAdGeneratorTaskStatusQuery($taskId: Long!) {
        adGeneratorTaskStatus(taskId: $taskId) {
            taskId
            state
            resultUrl
            errorMessage
            processingTimeMs
            createdAt
            completedAt
        }
    }
`;

const VideoGenerationTaskStatusQuery = graphql`
    query tasksVideoGenerationTaskStatusQuery($taskId: Long!) {
        videoGenerationTaskStatus(taskId: $taskId) {
            taskId
            state
            resultUrl
            errorMessage
            processingTimeMs
            createdAt
            completedAt
        }
    }
`;

const ProductStagingTaskStatusQuery = graphql`
    query tasksProductStagingTaskStatusQuery($taskId: Long!) {
        productStagingTaskStatus(taskId: $taskId) {
            taskId
            state
            resultUrl
            errorMessage
            processingTimeMs
            createdAt
            completedAt
        }
    }
`;

const UpscalerTaskStatusQuery = graphql`
    query tasksUpscalerTaskStatusQuery($taskId: Long!) {
        upscalerTaskStatus(taskId: $taskId) {
            taskId
            state
            resultUrl
            errorMessage
            processingTimeMs
            createdAt
            completedAt
        }
    }
`;

const ModelGeneration3DTaskStatusQuery = graphql`
    query tasksModelGeneration3DTaskStatusQuery($taskId: Long!) {
        modelGeneration3DTaskStatus(taskId: $taskId) {
            taskId
            state
            resultUrl
            errorMessage
            processingTimeMs
            createdAt
            completedAt
        }
    }
`;

// ============================================
// Helper Functions
// ============================================

const POLLING_INTERVAL = 2000; // 2 seconds
const MAX_POLLING_TIME = 600000; // 10 minutes timeout

type TaskState = 'WAITING' | 'PROCESSING' | 'COMPLETED' | 'ERROR';

interface TaskStatusResult {
    state: TaskState;
    resultUrl: string | null;
    errorMessage: string | null;
}

function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function createResult(tool: ToolType, url: string, error?: string): GenerationResult {
    return {
        id: generateId(),
        url: url || '',
        timestamp: new Date(),
        tool,
        status: error ? 'failed' : 'completed',
        error
    };
}

function createVideoResult(url: string, videoUrl: string, error?: string): VideoResult {
    return {
        id: generateId(),
        url: url || '',
        videoUrl: videoUrl || undefined,
        timestamp: new Date(),
        tool: 'video-generation',
        status: error ? 'failed' : 'completed',
        error,
        duration: 4
    };
}

async function pollTaskStatus<TQuery extends OperationType>(
    query: GraphQLTaggedNode,
    taskId: number,
    extractStatus: (data: TQuery['response']) => TaskStatusResult | null
): Promise<TaskStatusResult> {
    const startTime = Date.now();

    while (Date.now() - startTime < MAX_POLLING_TIME) {
        const data = await fetchQuery<TQuery>(RelayEnvironment, query, { taskId }).toPromise();

        const status = extractStatus(data!);
        if (!status) {
            throw new Error('Task not found');
        }

        if (status.state === 'COMPLETED' || status.state === 'ERROR') {
            return status;
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));
    }

    throw new Error('Task timeout');
}

// ============================================
// Generic Tool Task Executor
// ============================================

/**
 * Executes a tool task: starts a mutation, polls for status, and returns a GenerationResult.
 * Eliminates the duplicated pattern across all 7 tool functions.
 */
async function executeToolTask<TMutation extends MutationParameters, TStatusQuery extends OperationType>(config: {
    tool: ToolType;
    mutation: GraphQLTaggedNode;
    statusQuery: GraphQLTaggedNode;
    input: unknown;
    extractTaskId: (data: TMutation['response']) => number;
    extractStatus: (data: TStatusQuery['response']) => TaskStatusResult | null;
}): Promise<GenerationResult> {
    try {
        const response = await new Promise<TMutation['response']>((resolve, reject) => {
            commitMutation<TMutation>(RelayEnvironment, {
                mutation: config.mutation,
                variables: { input: config.input } as TMutation['variables'],
                onCompleted: resolve,
                onError: reject
            });
        });

        const taskId = config.extractTaskId(response);

        const status = await pollTaskStatus<TStatusQuery>(config.statusQuery, taskId, config.extractStatus);

        if (status.state === 'ERROR') {
            return createResult(config.tool, '', status.errorMessage || 'Task failed');
        }

        return createResult(config.tool, status.resultUrl || '');
    } catch (error) {
        console.error(`${config.tool} failed:`, error);
        return createResult(config.tool, '', error instanceof Error ? error.message : 'Unknown error');
    }
}

// ============================================
// API Functions
// ============================================

export async function virtualTryOn(params: VirtualTryOnInput): Promise<GenerationResult> {
    return executeToolTask<tasksStartVirtualTryOnMutation, tasksVirtualTryOnTaskStatusQuery>({
        tool: 'virtual-tryon',
        mutation: StartVirtualTryOnMutation,
        statusQuery: VirtualTryOnTaskStatusQuery,
        input: params,
        extractTaskId: (data) => data.startVirtualTryOn.taskId,
        extractStatus: (data) => data.virtualTryOnTaskStatus
    });
}

export async function magicSwap(params: MagicSwapInput): Promise<GenerationResult> {
    return executeToolTask<tasksStartMagicSwapMutation, tasksMagicSwapTaskStatusQuery>({
        tool: 'magic-swap',
        mutation: StartMagicSwapMutation,
        statusQuery: MagicSwapTaskStatusQuery,
        input: params,
        extractTaskId: (data) => data.startMagicSwap.taskId,
        extractStatus: (data) => data.magicSwapTaskStatus
    });
}

export async function multiImageFusion(params: MultiImageFusionInput): Promise<GenerationResult> {
    return executeToolTask<tasksStartMultiImageFusionMutation, tasksMultiImageFusionTaskStatusQuery>({
        tool: 'multi-image-fusion',
        mutation: StartMultiImageFusionMutation,
        statusQuery: MultiImageFusionTaskStatusQuery,
        input: params,
        extractTaskId: (data) => data.startMultiImageFusion.taskId,
        extractStatus: (data) => data.multiImageFusionTaskStatus
    });
}

export async function adGenerator(params: AdGeneratorInput): Promise<GenerationResult> {
    return executeToolTask<tasksStartAdGeneratorMutation, tasksAdGeneratorTaskStatusQuery>({
        tool: 'ad-generator',
        mutation: StartAdGeneratorMutation,
        statusQuery: AdGeneratorTaskStatusQuery,
        input: params,
        extractTaskId: (data) => data.startAdGenerator.taskId,
        extractStatus: (data) => data.adGeneratorTaskStatus
    });
}

export async function videoGeneration(params: VideoGenerationInput): Promise<VideoResult> {
    try {
        const response = await new Promise<tasksStartVideoGenerationMutation['response']>((resolve, reject) => {
            commitMutation<tasksStartVideoGenerationMutation>(RelayEnvironment, {
                mutation: StartVideoGenerationMutation,
                variables: { input: params },
                onCompleted: resolve,
                onError: reject
            });
        });

        const taskId = response.startVideoGeneration.taskId;

        const status = await pollTaskStatus<tasksVideoGenerationTaskStatusQuery>(
            VideoGenerationTaskStatusQuery,
            taskId,
            (data: tasksVideoGenerationTaskStatusQuery$data) => data.videoGenerationTaskStatus
        );

        if (status.state === 'ERROR') {
            return createVideoResult('', '', status.errorMessage || 'Task failed');
        }

        return createVideoResult(status.resultUrl || '', status.resultUrl || '');
    } catch (error) {
        console.error('Video Generation failed:', error);
        return createVideoResult('', '', error instanceof Error ? error.message : 'Unknown error');
    }
}

export async function productStaging(params: ProductStagingInput): Promise<GenerationResult> {
    return executeToolTask<tasksStartProductStagingMutation, tasksProductStagingTaskStatusQuery>({
        tool: 'product-staging',
        mutation: StartProductStagingMutation,
        statusQuery: ProductStagingTaskStatusQuery,
        input: params,
        extractTaskId: (data) => data.startProductStaging.taskId,
        extractStatus: (data) => data.productStagingTaskStatus
    });
}

export async function upscaler(params: UpscalerInput): Promise<GenerationResult> {
    return executeToolTask<tasksStartUpscalerMutation, tasksUpscalerTaskStatusQuery>({
        tool: 'upscaler',
        mutation: StartUpscalerMutation,
        statusQuery: UpscalerTaskStatusQuery,
        input: params,
        extractTaskId: (data) => data.startUpscaler.taskId,
        extractStatus: (data) => data.upscalerTaskStatus
    });
}

export async function modelGeneration3D(params: ModelGeneration3DInput, tool: ToolType): Promise<GenerationResult> {
    return executeToolTask<tasksStartModelGeneration3DMutation, tasksModelGeneration3DTaskStatusQuery>({
        tool,
        mutation: StartModelGeneration3DMutation,
        statusQuery: ModelGeneration3DTaskStatusQuery,
        input: params,
        extractTaskId: (data) => data.startModelGeneration3D.taskId,
        extractStatus: (data) => data.modelGeneration3DTaskStatus
    });
}

// ============================================
// Unified API Interface
// ============================================

export const aiStudioApi = {
    virtualTryOn,
    magicSwap,
    multiImageFusion,
    adGenerator,
    videoGeneration,
    productStaging,
    upscaler,
    modelGeneration3D
};

export default aiStudioApi;
